import React from "react";
import moment from "moment-timezone";
import { RIGHTSLINE_URL } from "tap/config/constants.js";
import { getOfferTableExcelExportColumns } from "tap/pages/Offers/components/grid/config/columnOptions.js";
const localFormat = "MM/DD/YYYY";

/** Formats a numeric value to 2 decimal places */
const formatDecimal = (v) => {
  const n = typeof v === "string" ? parseFloat(v) : v;
  return Number.isFinite(n) ? n.toFixed(2) : v;
};

/** Column configuration for visibility toggle */
export const COLUMN_OPTIONS = [
  { field: "historyCreatedAt", label: "History Created", defaultHidden: false },
  {
    field: "historyCreatedBy",
    label: "History Created By",
    defaultHidden: false,
  },
  { field: "modifiedAt", label: "Modified", defaultHidden: false },
  { field: "id", label: "ID", defaultHidden: false },
  { field: "tapOfferStatus", label: "TAP Offer Status", defaultHidden: false },
  { field: "offerTableRowRlId", label: "RL ID", defaultHidden: false },
  { field: "licensingJobId", label: "Licensing Job ID", defaultHidden: true },
  { field: "dealId", label: "Deal ID", defaultHidden: false },
  { field: "dealName", label: "Deal Name", defaultHidden: false },
  { field: "catalogId", label: "Catalog ID", defaultHidden: false },
  { field: "catalogName", label: "Catalog Name", defaultHidden: false },
  { field: "readyToPublish", label: "Ready to Publish", defaultHidden: false },
  { field: "offerStatus", label: "RL Offer Status", defaultHidden: false },
  { field: "dateTaken", label: "Date Taken", defaultHidden: false },
  { field: "media", label: "Media", defaultHidden: false },
  { field: "territory", label: "Territory", defaultHidden: false },
  { field: "languages", label: "Languages", defaultHidden: false },
  { field: "format", label: "Format", defaultHidden: false },
  { field: "termStart", label: "Term Start", defaultHidden: false },
  { field: "termEnd", label: "Term End", defaultHidden: false },
  {
    field: "startDateStatus",
    label: "Start Date Status",
    defaultHidden: false,
  },
  { field: "endDateStatus", label: "End Date Status", defaultHidden: false },
  { field: "rating", label: "Rating", defaultHidden: false },
  { field: "rateCardType", label: "Rate Card Type", defaultHidden: false },
  { field: "rateCardId", label: "Rate Card ID", defaultHidden: false },
  { field: "rateCardName", label: "Rate Card Name", defaultHidden: false },
  { field: "windows", label: "Window", defaultHidden: false },
  { field: "windowNum", label: "Window Number", defaultHidden: false },
  { field: "tier", label: "Tier", defaultHidden: false },
  { field: "licenseFee", label: "License Fee", defaultHidden: false },
  { field: "minDeemedPrice", label: "Min Deemed Price", defaultHidden: false },
  { field: "seasonPassAmt", label: "Season Pass Amt", defaultHidden: false },
  { field: "episodeTier", label: "Episode Tier", defaultHidden: false },
  { field: "pmxPlatform", label: "PMX Platform", defaultHidden: false },
  { field: "currency", label: "Currency", defaultHidden: false },
  { field: "percentageSplit", label: "Percentage Split", defaultHidden: false },
  { field: "emaPriceType", label: "EMA Price Type", defaultHidden: false },
  {
    field: "episodeEmaPriceType",
    label: "Episode EMA Price Type",
    defaultHidden: false,
  },
  { field: "viewingPeriod", label: "Viewing Period", defaultHidden: false },
  { field: "preSell", label: "Pre Sell", defaultHidden: false },
  { field: "subLanguage", label: "Sub Language", defaultHidden: false },
  { field: "dubLanguage", label: "Dub Language", defaultHidden: false },
  { field: "tprProgramName", label: "TPR Program Name", defaultHidden: false },
  { field: "ocad", label: "Consumer Announcement Date", defaultHidden: false },
  {
    field: "suppressionLiftDate",
    label: "Suppression Lift Date",
    defaultHidden: false,
  },
  { field: "exclusivity", label: "Exclusivity", defaultHidden: false },
  {
    field: "holdbackLanguages",
    label: "Holdback Languages",
    defaultHidden: false,
  },
  {
    field: "languageAvailsDisplay",
    label: "Language Avails Display",
    defaultHidden: false,
  },
  {
    field: "availsTypeOverride",
    label: "Avails Type Override",
    defaultHidden: false,
  },
  {
    field: "informationalRestrictions",
    label: "Informational Restrictions",
    defaultHidden: false,
  },
  { field: "message", label: "Message", defaultHidden: true },
];

/** Filterable columns configuration */
export const FILTER_COLUMNS = [
  { field: "tapOfferStatus", label: "TAP Offer Status" },
  { field: "readyToPublish", label: "Ready to Publish" },
  { field: "offerStatus", label: "RL Offer Status" },
  { field: "dealName", label: "Deal Name" },
  { field: "catalogName", label: "Catalog Name" },
  { field: "media", label: "Media" },
  { field: "territory", label: "Territory" },
  { field: "format", label: "Format" },
  { field: "rating", label: "Rating" },
  { field: "tier", label: "Tier" },
  { field: "episodeTier", label: "Episode Tier" },
  { field: "currency", label: "Currency" },
  { field: "pmxPlatform", label: "PMX Platform" },
  { field: "startDateStatus", label: "Start Date Status" },
  { field: "endDateStatus", label: "End Date Status" },
  { field: "rateCardType", label: "Rate Card Type" },
  { field: "rateCardName", label: "Rate Card Name" },
  { field: "exclusivity", label: "Exclusivity" },
];

/**
 * Format TAP Offer Status for display
 */
export const formatTapOfferStatus = (status) => {
  switch (status) {
    case "PENDING":
      return "Pending";
    case "PUSHED":
    case "OFFER_IN_PAST_SYSTEM_UPDATED":
      return "Pushed";
    case "DUPLICATE":
    case "DUPLICATE_DIFFJOB":
      return "Duplicate";
    case "SYSTEM_UPDATED":
      return "System Updated";
    case "USER_MODIFIED":
      return "User Modified";
    case "SYSTEM_UPDATED_OFFER_ACTIVE":
      return "Offer Active - System Updated";
    case "USER_MODIFIED_OFFER_ACTIVE":
      return "Offer Active - User Modified";
    case "INACTIVE_DEAL":
      return "Inactive Deal";
    case "BLANK_DEAL_FIELDS":
      return "Blank Deal Fields";
    case "RIGHTS_ISSUE":
      return "Rights Issue";
    case "MISSING_PRICING":
      return "Missing Pricing";
    case "TO_DELETE":
      return "To Delete";
    case "SYSTEM_CREATED":
      return "System Created";
    case "INFO_RESTRICTIONS":
      return "Info Restrictions";
    default:
      return status;
  }
};

/**
 * Format datetime for display
 */
export const formatDateTime = (value) => {
  if (!value) return "";
  return moment(moment.utc(value).toDate())
    .tz(moment.tz.guess())
    .format("DD-MMM-YYYY, h:mm:ss A");
};

const OFFER_HISTORY_EXCEL_PREFIX_FIELDS = [
  "historyCreatedAt",
  "historyCreatedBy",
  "modifiedAt",
];

const OFFER_HISTORY_EXCEL_PREFIX_GET_VALUE = {
  historyCreatedAt: (row) => formatDateTime(row.historyCreatedAt),
  historyCreatedBy: (row) => row.historyCreatedBy?.fullName || "",
  modifiedAt: (row) => formatDateTime(row.modifiedAt),
};

/**
 * Excel export column defs: history fields (labels from COLUMN_OPTIONS) + offer columns from shared Offers config.
 *
 * @param {boolean} hasEpisodeFeature
 * @returns {{ field: string, header: string, getValue?: (row: object) => unknown }[]}
 */
export function getOfferHistoryExcelExportColumnDefs(hasEpisodeFeature) {
  const prefix = OFFER_HISTORY_EXCEL_PREFIX_FIELDS.map((field) => ({
    field,
    header: COLUMN_OPTIONS.find((c) => c.field === field)?.label || field,
    getValue: OFFER_HISTORY_EXCEL_PREFIX_GET_VALUE[field],
  }));

  return [...prefix, ...getOfferTableExcelExportColumns(hasEpisodeFeature)];
}

/**
 * Format date for display
 */
export const formatDate = (value) => {
  if (!value) return "";
  return moment(moment(value).local().format(localFormat)).format(
    "DD-MMM-YYYY",
  );
};

/**
 * Compare changes between current row and next row
 * Returns: "CHANGED", "REMOVED", "ADDED", or "NO_CHANGES"
 */
export const compareChanges = (
  field,
  value,
  rowIndex,
  data,
  isDateField = false,
) => {
  if (rowIndex >= data.length - 1) {
    return "NO_CHANGES";
  }

  const nextRecord = data[rowIndex + 1];
  const nextValue = nextRecord?.[field];

  if (isDateField) {
    const currentDate = value ? formatDate(value) : null;
    const nextDate = nextValue ? formatDate(nextValue) : null;

    if (nextDate === currentDate) return "NO_CHANGES";
    if (nextDate !== null && (currentDate === null || currentDate === ""))
      return "REMOVED";
    if ((nextDate === null || nextDate === "") && currentDate !== null)
      return "ADDED";
    return "CHANGED";
  }

  let currentVal = value;
  let nextVal = nextValue;

  if (typeof currentVal === "string") currentVal = currentVal.toLowerCase();
  if (typeof nextVal === "string") nextVal = nextVal.toLowerCase();

  if (nextVal === currentVal) return "NO_CHANGES";
  if (nextVal !== null && (currentVal === null || currentVal === ""))
    return "REMOVED";
  if ((nextVal === null || nextVal === "") && currentVal !== null)
    return "ADDED";
  return "CHANGED";
};

/**
 * Get style based on change type
 */
export const getChangeStyle = (changeType) => {
  switch (changeType) {
    case "CHANGED":
      return {
        color: "blue",
        fontWeight: "bold",
        fontStyle: "italic",
        backgroundColor: "yellow",
      };
    case "REMOVED":
      return {
        color: "red",
        fontWeight: "bold",
        fontStyle: "italic",
        backgroundColor: "yellow",
      };
    case "ADDED":
      return {
        color: "green",
        fontWeight: "bold",
        fontStyle: "italic",
        backgroundColor: "yellow",
      };
    default:
      return {};
  }
};

/**
 * Cell renderer that shows change highlighting
 * Uses _originalIndex to compare against original data order
 */
export const ChangeCellRenderer = (
  field,
  formatFn = null,
  isDateField = false,
) => {
  return (params) => {
    const { value, data, context } = params;
    const originalData = context?.originalData || [];

    // Use _originalIndex stored on the row to find position in original data
    const originalIndex = data?._originalIndex ?? -1;

    const changeType = compareChanges(
      field,
      value,
      originalIndex,
      originalData,
      isDateField,
    );
    const style = getChangeStyle(changeType);

    let displayValue = value;
    if (changeType === "REMOVED") {
      displayValue = "REMOVED";
    } else if (formatFn && (value || value === 0)) {
      displayValue = formatFn(value);
    } else if (typeof value === "object" && value !== null) {
      displayValue = value.label || value.title || value;
    }

    return <span style={style}>{displayValue ?? ""}</span>;
  };
};

/**
 * Cell renderer for RightsLine link columns (Deal ID, Catalog ID)
 */
export const RightsLineLinkRenderer = (type) => {
  return (params) => {
    const { value } = params;
    if (!value) return "";

    const url = RIGHTSLINE_URL(type) + value;
    return (
      <a
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        style={{ color: "#3611c8", textDecoration: "underline" }}
      >
        {value}
      </a>
    );
  };
};

// ============================================================
// Individual Column Exports
// ============================================================

export const historyCreatedAt = {
  headerName: "History Created",
  field: "historyCreatedAt",
  minWidth: 200,
  sortable: true,
  cellRenderer: (params) => formatDateTime(params.value),
};

export const historyCreatedBy = {
  headerName: "History Created By",
  field: "historyCreatedBy",
  minWidth: 160,
  sortable: true,
  valueGetter: (params) => params.data?.historyCreatedBy?.fullName || "",
};

export const modifiedAt = {
  headerName: "Modified",
  field: "modifiedAt",
  minWidth: 200,
  sortable: true,
  cellRenderer: (params) => formatDateTime(params.value),
};

export const id = {
  headerName: "ID",
  field: "id",
  minWidth: 100,
  sortable: true,
};

export const tapOfferStatus = {
  headerName: "TAP Offer Status",
  field: "tapOfferStatus",
  minWidth: 200,
  sortable: true,
  cellRenderer: ChangeCellRenderer("tapOfferStatus", formatTapOfferStatus),
};

export const offerTableRowRlId = {
  headerName: "RL ID",
  field: "offerTableRowRlId",
  minWidth: 100,
  sortable: true,
  cellRenderer: ChangeCellRenderer("offerTableRowRlId"),
};

export const licensingJobId = {
  headerName: "Licensing Job ID",
  field: "licensingJobId",
  minWidth: 100,
  sortable: true,
  hide: true,
};

export const dealId = {
  headerName: "Deal ID",
  field: "dealId",
  minWidth: 100,
  sortable: true,
  cellRenderer: RightsLineLinkRenderer("deal"),
};

export const dealName = {
  headerName: "Deal Name",
  field: "dealName",
  minWidth: 300,
  sortable: true,
};

export const catalogId = {
  headerName: "Catalog ID",
  field: "catalogId",
  minWidth: 100,
  sortable: true,
  cellRenderer: RightsLineLinkRenderer("catalog"),
};

export const catalogName = {
  headerName: "Catalog Name",
  field: "catalogName",
  minWidth: 300,
  sortable: true,
};

export const readyToPublish = {
  headerName: "Ready to Publish",
  field: "readyToPublish",
  minWidth: 130,
  sortable: true,
  cellRenderer: ChangeCellRenderer("readyToPublish"),
};

export const offerStatus = {
  headerName: "Offer Status",
  field: "offerStatus",
  minWidth: 120,
  sortable: true,
  cellRenderer: ChangeCellRenderer("offerStatus"),
};

export const dateTaken = {
  headerName: "Date Taken",
  field: "dateTaken",
  minWidth: 120,
  sortable: true,
  cellRenderer: ChangeCellRenderer("dateTaken", formatDate, true),
};

export const media = {
  headerName: "Media",
  field: "media",
  minWidth: 150,
  sortable: true,
  cellRenderer: ChangeCellRenderer("media"),
};

export const territory = {
  headerName: "Territory",
  field: "territory",
  minWidth: 150,
  sortable: true,
  cellRenderer: ChangeCellRenderer("territory"),
};

export const languages = {
  headerName: "Languages",
  field: "languages",
  minWidth: 200,
  sortable: true,
  cellRenderer: ChangeCellRenderer(
    "languages",
    (v) => v?.replaceAll?.("|", " | ") || v,
  ),
};

export const format = {
  headerName: "Format",
  field: "format",
  minWidth: 150,
  sortable: true,
  cellRenderer: ChangeCellRenderer("format"),
};

export const termStart = {
  headerName: "Term Start",
  field: "termStart",
  minWidth: 150,
  sortable: true,
  cellRenderer: ChangeCellRenderer("termStart", formatDate, true),
};

export const termEnd = {
  headerName: "Term End",
  field: "termEnd",
  minWidth: 150,
  sortable: true,
  cellRenderer: ChangeCellRenderer("termEnd", formatDate, true),
};

export const startDateStatus = {
  headerName: "Start Date Status",
  field: "startDateStatus",
  minWidth: 150,
  sortable: true,
  cellRenderer: ChangeCellRenderer("startDateStatus"),
};

export const endDateStatus = {
  headerName: "End Date Status",
  field: "endDateStatus",
  minWidth: 150,
  sortable: true,
  cellRenderer: ChangeCellRenderer("endDateStatus"),
};

export const rating = {
  headerName: "Rating",
  field: "rating",
  minWidth: 150,
  sortable: true,
  cellRenderer: ChangeCellRenderer("rating"),
};

export const rateCardType = {
  headerName: "Rate Card Type",
  field: "rateCardType",
  minWidth: 150,
  sortable: true,
  cellRenderer: ChangeCellRenderer("rateCardType"),
};

export const rateCardId = {
  headerName: "Rate Card ID",
  field: "rateCardId",
  minWidth: 150,
  sortable: true,
};

export const rateCardName = {
  headerName: "Rate Card Name",
  field: "rateCardName",
  minWidth: 300,
  sortable: true,
};

export const windows = {
  headerName: "Window",
  field: "windows",
  minWidth: 150,
  sortable: true,
  valueFormatter: ({ value }) => {
    if (typeof value === "string") return value;
    if (Array.isArray(value))
      return value.map((w) => w.windowName || w.label || w).join(", ");
    if (typeof value === "object" && value !== null)
      return value.windowName || value.label || "";
    return value ?? "";
  },
  cellRenderer: ChangeCellRenderer("windows"),
};

export const windowNum = {
  headerName: "Window Number",
  field: "windowNum",
  minWidth: 150,
  sortable: true,
  cellRenderer: ChangeCellRenderer("windowNum"),
};

export const tier = {
  headerName: "Tier",
  field: "tier",
  minWidth: 100,
  sortable: true,
  cellRenderer: ChangeCellRenderer("tier"),
};

export const licenseFee = {
  headerName: "License Fee",
  field: "licenseFee",
  minWidth: 150,
  sortable: true,
  cellRenderer: ChangeCellRenderer("licenseFee", formatDecimal),
};

export const minDeemedPrice = {
  headerName: "Min Deemed Price",
  field: "minDeemedPrice",
  minWidth: 150,
  sortable: true,
  cellRenderer: ChangeCellRenderer("minDeemedPrice", formatDecimal),
};

export const seasonPassAmt = {
  headerName: "Season Pass Amt",
  field: "seasonPassAmt",
  minWidth: 150,
  sortable: true,
  cellRenderer: ChangeCellRenderer("seasonPassAmt", formatDecimal),
};

export const episodeTier = {
  headerName: "Episode Tier",
  field: "episodeTier",
  minWidth: 150,
  sortable: true,
  cellRenderer: ChangeCellRenderer("episodeTier"),
};

export const pmxPlatform = {
  headerName: "PMX Platform",
  field: "pmxPlatform",
  minWidth: 150,
  sortable: true,
  cellRenderer: ChangeCellRenderer("pmxPlatform"),
};

export const currency = {
  headerName: "Currency",
  field: "currency",
  minWidth: 150,
  sortable: true,
  cellRenderer: ChangeCellRenderer("currency"),
};

export const percentageSplit = {
  headerName: "Percentage Split",
  field: "percentageSplit",
  minWidth: 150,
  sortable: true,
  cellRenderer: ChangeCellRenderer("percentageSplit", (v) => {
    const n = typeof v === "string" ? parseFloat(v) : v;
    return Number.isFinite(n) ? `${n.toFixed(2)}%` : v;
  }),
};

export const emaPriceType = {
  headerName: "EMA Price Type",
  field: "emaPriceType",
  minWidth: 150,
  sortable: true,
  cellRenderer: ChangeCellRenderer("emaPriceType"),
};

export const episodeEmaPriceType = {
  headerName: "Episode EMA Price Type",
  field: "episodeEmaPriceType",
  minWidth: 180,
  sortable: true,
  cellRenderer: ChangeCellRenderer("episodeEmaPriceType"),
};

export const viewingPeriod = {
  headerName: "Viewing Period",
  field: "viewingPeriod",
  minWidth: 140,
  sortable: true,
  cellRenderer: ChangeCellRenderer("viewingPeriod"),
};

export const preSell = {
  headerName: "Pre Sell",
  field: "preSell",
  minWidth: 120,
  sortable: true,
  cellRenderer: ChangeCellRenderer("preSell", formatDate, true),
};

export const subLanguage = {
  headerName: "Sub Language",
  field: "subLanguage",
  minWidth: 180,
  sortable: true,
  cellRenderer: ChangeCellRenderer(
    "subLanguage",
    (v) => v?.replaceAll?.("|", " | ") || v,
  ),
};

export const dubLanguage = {
  headerName: "Dub Language",
  field: "dubLanguage",
  minWidth: 180,
  sortable: true,
  cellRenderer: ChangeCellRenderer(
    "dubLanguage",
    (v) => v?.replaceAll?.("|", " | ") || v,
  ),
};

export const tprProgramName = {
  headerName: "TPR Program Name",
  field: "tprProgramName",
  minWidth: 160,
  sortable: true,
  cellRenderer: ChangeCellRenderer("tprProgramName"),
};

export const ocad = {
  headerName: "Consumer Announcement Date",
  field: "ocad",
  minWidth: 200,
  sortable: true,
  cellRenderer: ChangeCellRenderer(
    "ocad",
    (v) => moment(v).format("DD-MMM-YYYY"),
    true,
  ),
};

export const suppressionLiftDate = {
  headerName: "Suppression Lift Date",
  field: "suppressionLiftDate",
  minWidth: 160,
  sortable: true,
  cellRenderer: ChangeCellRenderer("suppressionLiftDate", formatDate, true),
};

export const exclusivity = {
  headerName: "Exclusivity",
  field: "exclusivity",
  minWidth: 150,
  sortable: true,
};

export const holdbackLanguages = {
  headerName: "Holdback Languages",
  field: "holdbackLanguages",
  minWidth: 180,
  sortable: true,
  cellRenderer: ChangeCellRenderer(
    "holdbackLanguages",
    (v) => v?.replaceAll?.("|", " | ") || v,
  ),
};

export const languageAvailsDisplay = {
  headerName: "Language Avails Display",
  field: "languageAvailsDisplay",
  minWidth: 180,
  sortable: true,
  cellRenderer: ChangeCellRenderer("languageAvailsDisplay"),
};

export const availsTypeOverride = {
  headerName: "Avails Type Override",
  field: "availsTypeOverride",
  minWidth: 160,
  sortable: true,
  cellRenderer: ChangeCellRenderer("availsTypeOverride"),
};

export const informationalRestrictions = {
  headerName: "Informational Restrictions",
  field: "informationalRestrictions",
  minWidth: 300,
  sortable: true,
  cellRenderer: ChangeCellRenderer("informationalRestrictions"),
};

export const message = {
  headerName: "Message",
  field: "message",
  minWidth: 300,
  sortable: true,
  hide: true,
};
